import React, { useEffect, useState } from 'react';
import { JobFormFields } from 'parent-portal/forms/JobRequestTypes';
import { FieldStates } from '../../FormTypes';
import FormField from '../../FormField';
import JobFormPage from '../JobFormPage';
import { useJobFormContext } from 'parent-portal/forms/JobFormContext';
import { Text } from 'library';
import { client, consoleLogInDev } from 'shared';
import { JobQualificationValue, Qualification } from 'models/Qualifications';
import { Grid } from '@mui/material';
import JobDescriptionInput from '../HeadlineDescriptionPage/JobDescriptionInput';

export default function BusinessJobSummary({ fieldStates }: { fieldStates: FieldStates<JobFormFields> }) {
    const { submit } = useJobFormContext();
    const [qualifications, setQualifications] = useState<Qualification[]>([]);
    const [showDetails, setShowDetails] = useState(false);
    const getQualifications = () => {
        client('api/qualification/').then(setQualifications).catch(consoleLogInDev);
    };
    useEffect(() => {
        getQualifications();
    }, []);
    function inlineText(text: string, boldText?: string) {
        return (
            <Text inline variant="body2">
                {text}
                {boldText ? (
                    <Text inline bold variant="body2">
                        {boldText}
                    </Text>
                ) : null}
                .{' '}
            </Text>
        );
    }
    function booleanQualifications(selectedQualifications: JobQualificationValue[]) {
        let booleanQualifications = qualifications.filter((q) => q.value_type === 'BOOLEAN');
        return booleanQualifications.map((q: Qualification) => {
            let quali = selectedQualifications?.filter((qual) => qual.qualification === q.id);
            if (quali.length > 0) {
                return q.name;
            } else return null;
        });
    }

    function nonBooleanQualifications(selectedQualifications: JobQualificationValue[]) {
        let otherQualifications = qualifications.filter((q) => q.value_type !== 'BOOLEAN');
        return otherQualifications.map((q: Qualification) => {
            let quali = selectedQualifications?.filter((qual) => qual.qualification === q.id);
            if (['MULTI_SELECT', 'SINGLE_SELECT'].includes(q.value_type) && quali.length > 0) {
                let highlightedText = quali.map((s) => q.options?.find((o) => o.id === s.option)?.value);
                return inlineText(`${q.description}: `, highlightedText.join(', '));
            } else if (q.value_type === 'NUMBER' && quali.length > 0) {
                return inlineText(`${q.name}: `, quali[0].value as string);
            }
            return null;
        });
    }

    function populateQualifications() {
        let selectedQualifications: JobQualificationValue[] = fieldStates.jobQualifications.value ?? [];
        if (selectedQualifications.length > 0) {
            let otherQualificationStrings = nonBooleanQualifications(selectedQualifications);
            let booleanQualificationstrings = booleanQualifications(selectedQualifications);
            let finalStrings = [
                ...otherQualificationStrings.filter((s) => s !== null),
                inlineText(
                    'The care provider should be: ',
                    booleanQualificationstrings.filter((s) => s !== null).join(', '),
                ),
            ];
            return (
                <Text inline variant="body2">
                    {finalStrings.map((q, i) => (
                        <React.Fragment key={i}>{q}</React.Fragment>
                    ))}
                </Text>
            );
        } else {
            return null;
        }
    }

    const summaryPieces: { [key: string]: string | JSX.Element | null } = {
        headline: inlineText('This job is for ', fieldStates.headline.value),
        responsibilites: inlineText('The job responsibilities are: ', fieldStates.comments.value),
        requirements: populateQualifications(),
        pay: inlineText(
            'The pay rate is: $',
            fieldStates.pay.value.payMin === fieldStates.pay.value.payMax
                ? `${fieldStates.pay.value.payMin}/hr`
                : `${fieldStates.pay.value.payMin}-${fieldStates.pay.value.payMax}/hr`,
        ),
    };
    return (
        <JobFormPage title="Did we miss anything?" nextDisabled={false} onNextClicked={submit} nextText="Submit">
            <Text textStyle={{ marginTop: 10 }}>
                After reviewing the details below,{' '}
                <Text bold inline>
                    click &quot;Submit&quot;
                </Text>{' '}
                to post your job!
            </Text>
            <FormField title="Job Details" style={{ marginTop: 30 }}>
                <Text variant="body2" textStyle={{ marginTop: 10 }}>
                    {Object.keys(summaryPieces).map((k: string) => summaryPieces[k])}
                </Text>
            </FormField>
            <Grid style={{ marginTop: 20 }}>
                <Text
                    variant="body2"
                    bold
                    textStyle={{ textDecorationLine: 'underline', marginBottom: showDetails ? 20 : 0 }}
                    onClick={() => setShowDetails(!showDetails)}
                >
                    Add Other Details
                </Text>
                {showDetails ? <JobDescriptionInput business description={fieldStates.comments} /> : null}
            </Grid>
        </JobFormPage>
    );
}
